import * as React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  type TimeUnit,
  toDecimalHours,
  fromDecimalHours,
  formatHumanReadableTime,
  roundToQuarterHour,
  getStoredUnitPreference,
  setStoredUnitPreference,
  getInputConstraints,
} from "@/lib/timeUtils";

interface TimeInputProps {
  /** Value in decimal hours (same interface as current inputs) */
  value: string;
  /** Called with decimal hours string */
  onChange: (decimalHours: string) => void;
  /** Minimum hours allowed */
  minHours?: number;
  /** Maximum hours allowed */
  maxHours?: number;
  /** "default" shows hint below, "compact" shows hint as tooltip */
  variant?: "default" | "compact";
  /** Whether to show the human-readable hint (default: true) */
  showHint?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Additional className for the input */
  className?: string;
  /** Additional className for the wrapper */
  wrapperClassName?: string;
  /** Input id */
  id?: string;
  /** External error message */
  error?: string;
}

export function TimeInput({
  value,
  onChange,
  minHours = 0.25,
  maxHours = 24,
  variant = "default",
  showHint = true,
  placeholder,
  className,
  wrapperClassName,
  id,
  error,
}: TimeInputProps) {
  const [unit, setUnit] = React.useState<TimeUnit>(getStoredUnitPreference);
  const isUserInput = React.useRef(false);

  // Internal display value in the selected unit
  const [displayValue, setDisplayValue] = React.useState(() => {
    if (!value || value === "") return "";
    const hrs = parseFloat(value);
    if (isNaN(hrs) || hrs <= 0) return "";
    const initial = getStoredUnitPreference();
    return fromDecimalHours(hrs, initial).toString();
  });

  // Sync from parent when value changes externally (not from user input)
  React.useEffect(() => {
    // External resets (parent cleared the form) must always win, even if a
    // prior handleBlur left isUserInput.current=true via a no-op setState.
    if (!value || value === "") {
      isUserInput.current = false;
      setDisplayValue("");
      return;
    }
    if (isUserInput.current) {
      isUserInput.current = false;
      return;
    }
    const hrs = parseFloat(value);
    if (isNaN(hrs) || hrs <= 0) {
      setDisplayValue("");
      return;
    }
    const display = fromDecimalHours(hrs, unit);
    setDisplayValue(display.toString());
  }, [value, unit]);

  const handleUnitChange = (newUnit: TimeUnit) => {
    if (newUnit === unit) return;
    if (displayValue) {
      const num = parseFloat(displayValue);
      if (!isNaN(num) && num > 0) {
        const decimalHrs = toDecimalHours(num, unit);
        const newDisplay = fromDecimalHours(decimalHrs, newUnit);
        setDisplayValue(newDisplay.toString());
      }
    }
    setUnit(newUnit);
    setStoredUnitPreference(newUnit);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    isUserInput.current = true;
    if (raw === "" || raw === undefined) {
      setDisplayValue("");
      onChange("");
      return;
    }
    const regex = unit === "minutes" ? /^\d{0,4}$/ : /^\d{0,2}(\.\d{0,2})?$/;
    if (!regex.test(raw)) return;
    setDisplayValue(raw);
    const num = parseFloat(raw);
    if (!isNaN(num)) {
      onChange(toDecimalHours(num, unit).toString());
    }
  };

  const handleBlur = () => {
    if (displayValue === "" || displayValue === undefined) return;
    const num = parseFloat(displayValue);
    if (isNaN(num)) return;

    const decimalHrs = toDecimalHours(num, unit);
    const rounded = roundToQuarterHour(decimalHrs);
    const clamped = Math.max(0, Math.min(maxHours, rounded));
    const newDisplay = fromDecimalHours(clamped, unit);
    isUserInput.current = true;
    setDisplayValue(newDisplay.toString());
    onChange(clamped.toString());
  };

  const constraints = getInputConstraints(unit, { minHours, maxHours });

  const validationError = React.useMemo(() => {
    if (!displayValue || displayValue === "") return "";
    const num = parseFloat(displayValue);
    if (isNaN(num)) return "Enter a valid number";
    if (num > constraints.max)
      return unit === "minutes" ? `Maximum ${constraints.max} minutes` : `Maximum ${maxHours} hours`;
    if (num < 0) return unit === "minutes" ? "Minutes cannot be negative" : "Hours cannot be negative";
    return "";
  }, [displayValue, constraints.max, maxHours, unit]);

  const displayError = error || validationError;

  const decimalHours = value ? parseFloat(value) : 0;
  const hintText = decimalHours > 0 ? `= ${formatHumanReadableTime(decimalHours)}` : "";

  const isCompact = variant === "compact";
  const resolvedPlaceholder = placeholder || (unit === "minutes" ? "Min" : "Hrs");

  return (
    <div>
      <div className={cn("flex items-center gap-1.5", wrapperClassName)}>
        <Input
          id={id}
          type="text"
          inputMode={unit === "minutes" ? "numeric" : "decimal"}
          placeholder={resolvedPlaceholder}
          value={displayValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          className={cn(
            "flex-1 min-w-0",
            isCompact ? "text-sm h-8" : "",
            displayError ? "border-destructive focus-visible:ring-destructive" : "",
            className,
          )}
          aria-invalid={!!displayError}
        />
        <div className={cn(
          "flex rounded-md border border-input overflow-hidden shrink-0",
          isCompact ? "h-8" : "h-9"
        )}>
          <button
            type="button"
            onClick={() => handleUnitChange("hours")}
            className={cn(
              "px-2 font-medium transition-colors",
              isCompact ? "text-[10px]" : "text-xs",
              unit === "hours"
                ? "bg-primary text-primary-foreground"
                : "bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            )}
          >
            Hr
          </button>
          <button
            type="button"
            onClick={() => handleUnitChange("minutes")}
            className={cn(
              "px-2 font-medium transition-colors",
              isCompact ? "text-[10px]" : "text-xs",
              unit === "minutes"
                ? "bg-primary text-primary-foreground"
                : "bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            )}
          >
            Min
          </button>
        </div>
      </div>
      {displayError && (
        <p className={cn("font-medium text-destructive mt-1", isCompact ? "text-[10px]" : "text-xs")}>
          {displayError}
        </p>
      )}
      {!displayError && showHint && hintText && (
        <p className={cn("text-muted-foreground mt-1", isCompact ? "text-[10px]" : "text-xs")}>
          {hintText}
        </p>
      )}
    </div>
  );
}
